import { Typography } from '@euler/app/components/typography';
import { useAuthTheme } from '@euler/app/flows/auth/hooks';
import { TVFocusable } from '@euler/components/tvos/TVFocusable';
import { oemConfig } from '@euler/config/oem';
import {
  kPrivacyPolicyUrl,
  kTermsOfServiceUrl,
} from '@euler/functions/constants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import * as WebBrowser from 'expo-web-browser';
import { FC, PropsWithChildren, memo, useCallback, useMemo } from 'react';
import {
  Insets,
  StyleProp,
  TextStyle,
  TouchableOpacity,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native';
import Checkbox from 'react-native-bouncy-checkbox';

const CheckboxTouchable: FC<
  PropsWithChildren<ViewProps> & { inset: number }
> = ({ inset, children, ...props }) => {
  const hitSlop = useMemo<Insets>(
    () => ({
      left: inset,
      top: inset,
      right: inset,
      bottom: inset,
    }),
    [inset],
  );
  return (
    <TouchableOpacity {...props} hitSlop={hitSlop}>
      {children}
    </TouchableOpacity>
  );
};

export const LoginAgreement: FC<{
  checked: boolean;
  style?: StyleProp<ViewStyle>;
  linkStyle?: StyleProp<TextStyle>;
  checkboxColor?: string;
  checkboxSize?: number;
  labelStyle?: StyleProp<TextStyle>;
  label?: string;
  onChange: (value: boolean) => void;
}> = memo(props => {
  const theme = useAuthTheme();
  const { rpx } = useResponsiveContext();

  const {
    checked,
    onChange,
    labelStyle,
    linkStyle,
    style,
    checkboxSize = rpx(18),
    checkboxColor = theme.agreementCheckBoxFillColor,
    label = '同意',
  } = props;

  const openWebPage = useCallback((url: string) => {
    WebBrowser.openBrowserAsync(url).catch(() => null);
  }, []);

  const onCheckboxPress = useCallback(() => {
    onChange(!checked);
  }, [onChange, checked]);

  const labelFontSize = rpx(14);

  return (
    <View
      style={style}
      css={`
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
      `}
    >
      <Checkbox
        disableText
        disableBuiltInState
        isChecked={checked}
        fillColor={checkboxColor}
        onPress={onCheckboxPress}
        size={checkboxSize}
        TouchableComponent={CheckboxTouchable}
      />
      <View
        css={`
          margin-left: ${rpx(8)}px;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
        `}
      >
        <Typography.Label
          light
          color={theme.agreementLabelTextColor}
          size={labelFontSize}
          style={labelStyle}
        >
          {label}
        </Typography.Label>
        <TVFocusable
          onPress={() => {
            openWebPage(oemConfig.termsOfServiceUrl ?? kTermsOfServiceUrl);
          }}
        >
          <Typography.Label
            light
            color={theme.linkTextColor}
            size={labelFontSize}
            style={[labelStyle, linkStyle]}
          >
            用户协议
          </Typography.Label>
        </TVFocusable>
        <Typography.Label
          light
          color={theme.agreementLabelTextColor}
          size={labelFontSize}
          style={labelStyle}
        >
          和
        </Typography.Label>
        <TVFocusable
          onPress={() => {
            openWebPage(oemConfig.privacyPolicyUrl ?? kPrivacyPolicyUrl);
          }}
        >
          <Typography.Label
            light
            color={theme.linkTextColor}
            size={labelFontSize}
            style={[labelStyle, linkStyle]}
          >
            隐私条款
          </Typography.Label>
        </TVFocusable>
      </View>
    </View>
  );
});
